/*
 * WP-10 appearance bundle (§14A.9): fetch the published document at boot,
 * apply editable theme tokens as CSS custom properties before first paint,
 * and warm-cache the document in localStorage for instant paint on revisit.
 */
import { useSyncExternalStore } from 'react';
import { API_BASE } from '../api/portal-api';
import type { AppearanceBundle } from '../api/types';
import type { LocalisedText } from '@humidor/shared';

const CACHE_KEY = 'hp.appearance.v1';
const REVALIDATE_MS = 60_000;

let bundle: AppearanceBundle | null = null;
let inflight: Promise<AppearanceBundle | null> | null = null;
let lastFetch = 0;
/* F3-C — mounted consumers re-render when a new authoritative bundle
 * lands (or an authoritative reset clears it): applyAppearance bumps
 * `version`, useSyncExternalStore notifies subscribers. */
let version = 0;
const listeners = new Set<() => void>();

type CachedBundle = { fetchedAt: number; bundle: AppearanceBundle };

/* Variables the appearance layer OWNS on :root — they must mirror the
 * authoritative bundle exactly. Anything else (display prefs, unrelated
 * inline styles, locked theme tokens) is never touched. R5 split:
 * --hp-accent is the editable CONTENT/decorative accent; application
 * identity (--hp-identity) and action (--hp-action*) tokens are governed
 * by the stylesheet, so a published bundle re-themes content surfaces
 * without moving product action colour or breaking its contrast. */
function setOrRemove(root: HTMLElement, name: string, value: string | undefined): void {
  if (value) root.style.setProperty(name, value);
  else root.style.removeProperty(name);
}

/** Applies the theme tokens to :root (idempotent). `null` = the
 * authoritative "nothing published" reset — every owned override is
 * removed so the canonical stylesheet supplies defaults again. */
export function applyAppearance(b: AppearanceBundle | null): void {
  const theme = b?.document?.theme;
  const root = document.documentElement;
  setOrRemove(root, '--hp-paper', theme?.paper);
  setOrRemove(root, '--hp-paper-2', theme?.paper2);
  setOrRemove(root, '--hp-accent', theme?.accent);
  /* --hp-accent-soft is DERIVED from --hp-accent: when the source
   * override is removed the derived variable resets with it. */
  setOrRemove(root, '--hp-accent-soft', theme?.accent ? `${theme.accent}14` : undefined);
  bundle = b ?? null;
  version += 1;
  listeners.forEach((cb) => cb());
}

/** React subscription for appearance consumers — bumps on every apply. */
export function subscribeAppearance(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/** Re-renders the caller when the module bundle changes; returns it. */
export function useAppearance(): AppearanceBundle | null {
  useSyncExternalStore(subscribeAppearance, () => version);
  return bundle;
}

/** Synchronous warm path — call before first render. */
export function applyCachedAppearance(): void {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return;
    const cached = JSON.parse(raw) as CachedBundle;
    if (cached?.bundle) applyAppearance(cached.bundle);
  } catch {
    /* corrupt cache — ignore */
  }
}

/* The API signals the authoritative "nothing published" state as
 * 404 + { code: 'APPEARANCE_NOT_PUBLISHED' } — ONLY that envelope is a
 * legitimate reset. Any other non-OK status, unreadable body or network
 * failure is a transport problem: the last known bundle and warm cache
 * stay, so a failed fetch can never masquerade as a published reset. */
async function isAuthoritativeUnpublished(res: Response): Promise<boolean> {
  if (res.status !== 404) return false;
  try {
    const body = (await res.clone().json()) as { code?: string };
    return body?.code === 'APPEARANCE_NOT_PUBLISHED';
  } catch {
    return false;
  }
}

/** Fetches the bundle (cached ≤60s) and applies it. */
export async function loadAppearance(force = false): Promise<AppearanceBundle | null> {
  if (bundle && !force && Date.now() - lastFetch < REVALIDATE_MS) return bundle;
  inflight ??= fetch(`${API_BASE}/public/appearance`, { credentials: 'same-origin' })
    .then(async (res) => {
      if (!res.ok) {
        if (!(await isAuthoritativeUnpublished(res))) return bundle;
        lastFetch = Date.now();
        applyAppearance(null);
        try {
          localStorage.removeItem(CACHE_KEY);
        } catch {
          /* storage denied — the module reset still stands */
        }
        return null;
      }
      const data = (await res.json()) as AppearanceBundle;
      lastFetch = Date.now();
      applyAppearance(data);
      try {
        localStorage.setItem(
          CACHE_KEY,
          JSON.stringify({ fetchedAt: lastFetch, bundle: data } satisfies CachedBundle),
        );
      } catch {
        /* storage full — cosmetic only */
      }
      return data;
    })
    .catch(() => bundle)
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

export function getAppearance(): AppearanceBundle | null {
  return bundle;
}

/** Resolve a LocalisedText field for the active locale (en → any → null). */
export function lt(
  text: LocalisedText | null | undefined,
  locale: string,
): string | null {
  if (!text) return null;
  const map = text as unknown as Record<string, string | undefined>;
  const value = map[locale] ?? map.en ?? Object.values(map).find((v) => v);
  return typeof value === 'string' && value.trim() ? value : null;
}

/** Resolves an appearance assetId (or URL) to a public media URL. */
export function mediaUrl(
  ref: string | { url?: string } | null | undefined,
): string | null {
  if (!ref) return null;
  if (typeof ref !== 'string') return ref.url ?? null;
  if (ref.startsWith('/')) return ref;
  const asset = bundle?.assets?.[ref];
  return asset?.url ?? null;
}

/** Resolves a localised asset field (alt/caption) for the active locale. */
export function localisedAssetField(
  assetId: string | null | undefined,
  field: 'alt' | 'caption',
  locale: string,
): string | null {
  if (!assetId) return null;
  const map = bundle?.assets?.[assetId]?.[field];
  if (!map) return null;
  return map[locale] ?? map.en ?? Object.values(map)[0] ?? null;
}

/** Shop appearance profile (hero, localised display/address/hours, closed days). */
export function shopProfile(shopId: string | null | undefined) {
  if (!shopId) return null;
  return bundle?.document?.shops?.[shopId] ?? null;
}

/** Box picture override from document.products[productId].boxPictureAssetId. */
export function productBoxPicture(productId: string | null | undefined): string | null {
  if (!productId) return null;
  const ref = bundle?.document?.products?.[productId]?.boxPictureAssetId;
  return mediaUrl(ref);
}

/** Brand mark override from document.brands[brandId].markAssetId. */
export function brandMark(brandId: string | null | undefined): string | null {
  if (!brandId) return null;
  const ref = bundle?.document?.brands?.[brandId]?.markAssetId;
  return mediaUrl(ref);
}

export type ResolvedAnnouncement = {
  style: 'info' | 'notice';
  body: string;
  linkLabel: string | null;
  linkUrl: string | null;
};

/** Enabled announcement within its display window, resolved for the locale. */
export function activeAnnouncement(locale: string): ResolvedAnnouncement | null {
  const a = bundle?.document?.announcement;
  if (!a || !a.enabled) return null;
  const now = Date.now();
  if (a.startsAt && Date.parse(a.startsAt) > now) return null;
  if (a.endsAt && Date.parse(a.endsAt) <= now) return null;
  const body = lt(a.text, locale);
  if (!body) return null;
  return {
    style: a.style,
    body,
    linkLabel: a.linkUrl ? lt(a.linkLabel, locale) : null,
    linkUrl: a.linkUrl,
  };
}
